import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { BrandIcons, BrandTints } from '../pack-icons/brands';

/**
 * Format a numeric metric for compact display.
 *  1234567 → "1.2M"
 *    14823 → "15k"
 *      842 → "842"
 */
export function formatMetric(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
  return String(n);
}

const STATUS_META = {
  published: { label: 'Published', cls: 'pill-success' },
  scheduled: { label: 'Scheduled', cls: 'pill-info' },
  planned: { label: 'Planned', cls: 'pill-info' },
  ready: { label: 'Ready', cls: 'pill-info' },
  publishing: { label: 'Publishing', cls: 'pill-info' },
  draft: { label: 'Draft', cls: '' },
  failed: { label: 'Failed', cls: 'pill-warning' },
  attention: { label: 'Needs attention', cls: 'pill-warning' },
  generating: { label: 'Generating', cls: 'pill-info' },
};

/**
 * LibraryCard — pack-aligned post card.
 *
 * Props:
 *   - id, platform, status, title, snippet, date (display string), media ('image'|'carousel'|'video'|'thread'),
 *     metrics ({ views, likes, comments, shares, engagement }), serif (italic title styling)
 *   - onEdit, onDuplicate, onPreview, onMore, onAction (status-specific CTA)
 *
 * Hover quick-action buttons stop propagation so they do not navigate via the cover Link.
 */
export default function LibraryCard({
  id,
  platform = 'instagram',
  status = 'draft',
  title = '',
  snippet = '',
  date = '',
  media,
  metrics,
  imageUrl,
  serif = false,
  href,
  onEdit,
  onDuplicate,
  onPreview,
  onMore,
  onAction,
  actionLabel,
}) {
  const [hover, setHover] = useState(false);
  const sm = STATUS_META[status] || STATUS_META.draft;
  const tint = BrandTints[platform] || BrandTints.x;
  const gradient = `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)`;
  const coverStyle = imageUrl
    ? { background: `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%), url("${imageUrl}") center/cover no-repeat` }
    : { background: gradient };
  const coverHref = href || (id != null ? `/app/post/${id}` : '#');
  const hasMetrics = metrics && (
    metrics.views > 0
    || metrics.likes > 0
    || metrics.comments > 0
    || metrics.shares > 0
  );

  return (
    <article
      className={`lib-card lib-card-${status}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Link to={coverHref} className="lib-cover" style={coverStyle}>
        <span className={`lib-plat-ic ob-net-ic-${platform}`}>
          {BrandIcons[platform]?.({ size: 18 })}
        </span>

        <span className={`pill ${sm.cls} lib-status`}>
          <span className="dot" />
          {sm.label}
        </span>

        {media && (
          <span className="lib-media mono">
            {media === 'video' && (<><I.play size={10} /> Video</>)}
            {media === 'carousel' && (<><I.copy size={9} /> Carousel</>)}
            {media === 'thread' && 'Thread'}
            {media === 'image' && 'Image'}
          </span>
        )}

        <div
          className="lib-cover-text"
          style={serif ? { fontFamily: 'var(--font-serif)', fontStyle: 'italic' } : undefined}
        >
          {title}
        </div>

        {hover && (
          <div
            className="lib-quick"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          >
            {onEdit && (<button title="Edit" onClick={onEdit} type="button"><I.edit size={13} /></button>)}
            {onDuplicate && (<button title="Duplicate" onClick={onDuplicate} type="button"><I.copy size={13} /></button>)}
            {onPreview && (<button title="Preview" onClick={onPreview} type="button"><I.eye size={13} /></button>)}
            {onMore && (<button title="More" onClick={onMore} type="button"><I.dots size={13} /></button>)}
          </div>
        )}
      </Link>

      <div className="lib-body">
        {snippet && <p className="lib-snippet">{snippet}</p>}

        {hasMetrics ? (
          <div className="lib-metrics">
            <div className="lib-metric">
              <I.eye size={12} />
              <span className="mono">{formatMetric(metrics?.views)}</span>
            </div>
            <div className="lib-metric">
              <I.heart size={12} />
              <span className="mono">{formatMetric(metrics?.likes)}</span>
            </div>
            <div className="lib-metric">
              <I.chat size={12} />
              <span className="mono">{formatMetric(metrics?.comments)}</span>
            </div>
            <div className="lib-metric">
              <I.share size={12} />
              <span className="mono">{formatMetric(metrics?.shares)}</span>
            </div>
            {metrics?.engagement != null && (
              <div className="lib-metric lib-eng">
                <I.trend size={12} />
                <span className="mono">{Number(metrics.engagement).toFixed(1)}%</span>
              </div>
            )}
          </div>
        ) : (
          <div className="lib-no-metrics">
            <I.clock size={11} />
            <span>Metrics appear after publish</span>
          </div>
        )}

        <div className="lib-foot">
          <span className="lib-date mono">
            <I.calendar size={11} />
            {date}
          </span>
          {actionLabel && onAction && (
            <button
              className={`btn ${status === 'published' ? 'btn-ghost' : 'btn-soft'} btn-sm`}
              onClick={onAction}
              type="button"
            >
              {actionLabel}
              {status !== 'published' && <I.arrowRight size={11} />}
              {status === 'published' && <I.external size={11} />}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
